/*
 * File: MeTooAbout.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 9, 2025 at 5:09 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 31, 2025 at 4:03 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// Components
import {
	Meta,
	AppNavigation,
	SchemaScript,
	Typography,
	LocaleLink,
} from '../../../../../src/components';
import { buildDynamicMeta } from '../../../../../src/components/Meta';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../../constants/base.constants';

// Styles
import * as S from './MeTooAbout.styles';


/**
 * Component
 */

const MeTooAbout = ({ meta: m }) => {

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageMeTooAbout.metaTitle', { partnerName: partner.name }),
		description: t('pageMeTooAbout.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		...m || {}
	}), [m, partner, t]);

	// Create partner domain
	const partnerDomain = useMemo(() => {
		let domain = `https://${partner.domain}`;
		if (partner.redirect_to_subdomain) {
			domain = `https://${partner.redirect_to_subdomain}.${partner.domain}`;
		}
		return domain;
	}, [partner]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Schema.org */}
			<SchemaScript schema={{
				'@context': 'http://schema.org',
				'@type': ['WebPage', 'AboutPage'],
				'@id': `${partnerDomain}/about/#WebPage`,
				name: buildDynamicMeta(meta.title, { partner }),
				description: buildDynamicMeta(meta.description, { partner }),
				url: `${partnerDomain}${locale.localePath}/about`,
				inLanguage: locale.localeBaseId,
				isPartOf: {
					'@id': `${partnerDomain}/#WebSite`
				},
				publisher: {
					'@id': `${partner.website}/#Organization`,
				},
			}}
			/>

			{/* Component Content */}
			<AppNavigation showFooter>
				<S.Wrapper>

					{/* Introduction */}
					<S.PartnerHeroSection>
						<img className="me-too-logo" src="https://metoomvmt.org/wp-content/themes/metoo/images/metoo-logo-circle.svg" alt="me too. International logo" title="me too. International logo" />
						<Typography tag="p">
							<Typography className="partner-emphasis" weight="bold">The me too. Movement</Typography>
							{' '}
							believes in the radical possibilities of a
							movement against sexual violence, led by survivors of sexual violence. When survivors channel their unique
							empathy in community with one another, and in service of a future free from sexual violence, that’s when
							change happens.
						</Typography>
					</S.PartnerHeroSection>

					{/* Vision */}
					<S.PartnerSection>
						<Typography tag="h2" weight="bold">
							Vision
						</Typography>
						<Typography tag="p">
							<LocaleLink title="me too. International" aria-label="me too. International" to="https://metoomvmt.org/" target="_blank" rel="noreferrer">me too. International</LocaleLink>
							{' '}
							envisions a world free of sexual
							violence. We assert that the transformation of oppressive systems, narratives, and cultures, grounded in the
							lived experiences and leadership of survivors, creates space for generative ideas, practices, and relationships
							rooted in love, respect, empathy, and wellness. We imagine communities that are abundant with joy, safety, and
							resources and committed to radical healing, and we are committed to the work to live toward this vision.
						</Typography>
					</S.PartnerSection>

					{/* Mission */}
					<S.PartnerSection>
						<Typography tag="h2" weight="bold">
							Mission
						</Typography>
						<Typography tag="p">
							me too. serves as a convener, thought leader, and organizer across the mainstream and the grassroots to address systems
							that allow for the proliferation of sexual violence, specifically in Black, queer, trans, disabled, and all communities
							of color. Leveraging its model and framework, grounded in existing research and theory, ‘me too.’ centers individual
							and community healing and transformation, empowerment through empathy, shifting cultural narratives and practices
							and advancing a global survivor-led movement to end sexual violence.
						</Typography>
					</S.PartnerSection>

				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

MeTooAbout.propTypes = {
	meta: PropTypes.shape()
};
MeTooAbout.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default MeTooAbout;
